import { useEffect, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api, { getApiError } from '../../lib/axios';
import { Search, Upload, Trash2, Loader2, ChevronLeft, ChevronRight, X, CheckCircle } from 'lucide-react';

interface AdminDomain {
  id: string;
  domain: string;
  dr: number | null;
  ref_domains: number | null;
  backlinks: number | null;
  organic_traffic: number | null;
  topics: string | null;
  language: string | null;
  country: string | null;
  listing_count: number;
}

export default function AdminDomains() {
  const [domains, setDomains] = useState<AdminDomain[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const perPage = 50;

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
      if (search) params.set('search', search);
      const res = await api.get(`/api/admin/domains?${params}`);
      setDomains(res.data.domains);
      setTotal(res.data.total);
    } catch (err: any) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDomains(); }, [page]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchDomains();
  };

  const handleImport = async (file: File) => {
    setImporting(true);
    setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/api/admin/domains/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      fetchDomains();
    } catch (err: any) {
      setError(getApiError(err, 'Import failed'));
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete domain ${name}?`)) return;
    setDeletingId(id);
    try {
      await api.delete(`/api/admin/domains/${id}`);
      setDomains(prev => prev.filter(d => d.id !== id));
      setTotal(prev => prev - 1);
    } catch (err: any) {
      setError(getApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  const formatNum = (n: number | null) => {
    if (n === null) return '—';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Domain Database</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{total.toLocaleString()} domains</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setShowImport(true); setImportResult(null); }}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg flex items-center gap-2"
            >
              <Upload className="w-4 h-4" /> Import CSV
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
            {error}
            <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">×</button>
          </div>
        )}

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search domains..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg">
            Search
          </button>
        </form>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DR</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RefDomains</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Backlinks</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Traffic</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Listings</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {domains.map(d => (
                    <tr key={d.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{d.domain}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{d.dr ?? '—'}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatNum(d.ref_domains)}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatNum(d.backlinks)}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatNum(d.organic_traffic)}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{d.country || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300 rounded-full text-xs font-medium">
                          {d.listing_count}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDelete(d.id, d.domain)}
                          disabled={deletingId === d.id}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          {deletingId === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Import Modal */}
        {showImport && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowImport(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Import Domains CSV</h2>
                <button onClick={() => setShowImport(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Upload a CSV with columns: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">domain, dr, ref_domains, backlinks, organic_traffic, topics, language, country, price, seller_name</code>.
                Only <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded text-xs">domain</code> is required.
              </p>

              {importResult ? (
                <div className="space-y-3">
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="font-medium text-green-700 dark:text-green-400">Import Complete</span>
                    </div>
                    <div className="text-sm text-green-600 dark:text-green-400 space-y-1">
                      <p>Domains created: {importResult.domains_created}</p>
                      <p>Domains updated: {importResult.domains_updated}</p>
                      <p>Listings created: {importResult.listings_created}</p>
                    </div>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-sm text-yellow-700 dark:text-yellow-400">
                      <p className="font-medium mb-1">{importResult.errors.length} errors:</p>
                      {importResult.errors.slice(0, 5).map((e: string, i: number) => <p key={i}>{e}</p>)}
                    </div>
                  )}
                  <button onClick={() => setShowImport(false)} className="w-full py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-medium text-sm">
                    Done
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                  {importing ? (
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="w-8 h-8 animate-spin text-brand-500" />
                      <p className="text-sm text-gray-500">Importing...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                      <label className="inline-flex items-center px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg cursor-pointer">
                        Choose CSV File
                        <input type="file" accept=".csv" onChange={e => e.target.files?.[0] && handleImport(e.target.files[0])} className="hidden" />
                      </label>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
